import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Chip,
  Stack,
  Divider,
  Tooltip,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RemoveIcon from '@mui/icons-material/Remove';
import VisibilityIcon from '@mui/icons-material/Visibility';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import DescriptionIcon from '@mui/icons-material/Description';
import { listDashboardConfigs, createDashboardConfig, updateDashboardConfig, deleteDashboardConfig, getDashboardConfig, duplicateDashboardConfig } from '../api/dashboardConfig.api';
import { APP_TEMPLATES } from '../data/appTemplates';
import { getUserFlows } from '../api/agenticFlow.api';
import { COMPONENT_TYPES, OUTPUT_KEYS, COMPONENT_REGISTRY, getDefaultOutputKey } from '../components/Dashboard/controlRegistry';

export default function DashboardBuilderPage({ darkMode }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get('edit');

  const [list, setList] = useState([]);
  const [flows, setFlows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [flowsLoading, setFlowsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [openDialogOpen, setOpenDialogOpen] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewDashboard, setPreviewDashboard] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', defaultFlowId: '', layout: 'three-panel', chatPosition: 'header' });
  const [saving, setSaving] = useState(false);
  const [panelForm, setPanelForm] = useState(null);
  const [panelDialogOpen, setPanelDialogOpen] = useState(false);
  const [addPanelDialogOpen, setAddPanelDialogOpen] = useState(false);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [addPanelForm, setAddPanelForm] = useState({ dashboardId: null, position: 'left', componentType: 'table', outputKey: 'table_data', flowId: '', chartType: 'bar', refreshOnMount: false });

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const dashRes = await listDashboardConfigs();
      setList(dashRes.data ?? []);
    } catch (err) {
      setError(err?.message || 'Failed to load dashboards');
    } finally {
      setLoading(false);
    }
  };

  const loadFlows = async () => {
    setFlowsLoading(true);
    try {
      const flowRes = await getUserFlows({});
      console.log('🔍 Flow API Response (full):', JSON.stringify(flowRes, null, 2));
      
      // Handle different response structures
      let flowList = [];
      
      // Check for error first
      if (flowRes?.err) {
        console.error('❌ Flow API error:', flowRes.err);
        setFlows([]);
        setFlowsLoading(false);
        return;
      }
      
      // The API returns { success: true, flows: [...] }
      // axiosClient.get() interceptor returns res.data, so getUserFlows returns { response: { success: true, flows: [...] } }
      const response = flowRes?.response;
      
      if (response) {
        console.log('🔍 Response object:', response);
        console.log('🔍 Response.flows:', response.flows);
        console.log('🔍 Is flows array?', Array.isArray(response.flows));
        
        // Most common case: response.flows is the array
        if (Array.isArray(response.flows)) {
          flowList = response.flows;
          console.log('✅ Found flows in response.flows:', flowList.length);
        }
        // If response is the flows array itself
        else if (Array.isArray(response)) {
          flowList = response;
          console.log('✅ Response is array:', flowList.length);
        }
        // If response has nested data structure
        else if (response.data) {
          if (Array.isArray(response.data.flows)) {
            flowList = response.data.flows;
            console.log('✅ Found flows in response.data.flows:', flowList.length);
          } else if (Array.isArray(response.data)) {
            flowList = response.data;
            console.log('✅ Found flows in response.data:', flowList.length);
          }
        }
        // Try to find flows property in response object
        else if (typeof response === 'object' && response !== null) {
          // Check all keys for arrays
          const keys = Object.keys(response);
          console.log('🔍 Response keys:', keys);
          for (const key of keys) {
            if (Array.isArray(response[key])) {
              flowList = response[key];
              console.log(`✅ Found flows array in key "${key}":`, flowList.length);
              break;
            }
          }
        }
      }
      // If flowRes is directly the response object (no .response wrapper)
      else if (flowRes && typeof flowRes === 'object' && !flowRes.err) {
        if (Array.isArray(flowRes.flows)) {
          flowList = flowRes.flows;
          console.log('✅ Found flows in flowRes.flows:', flowList.length);
        } else if (Array.isArray(flowRes.data)) {
          flowList = flowRes.data;
          console.log('✅ Found flows in flowRes.data:', flowList.length);
        } else if (Array.isArray(flowRes)) {
          flowList = flowRes;
          console.log('✅ flowRes is array:', flowList.length);
        }
      }
      
      if (flowList.length === 0) {
        console.warn('⚠️ No flows found in response. Response structure:', flowRes);
      } else {
        console.log('✅ Successfully extracted flows:', flowList.length, 'flows');
      }
      
      setFlows(flowList);
    } catch (err) {
      console.error('❌ Failed to load flows:', err);
      setFlows([]);
    } finally {
      setFlowsLoading(false);
    }
  };

  useEffect(() => {
    load();
    loadFlows();
  }, []);

  useEffect(() => {
    if (editId && list.length) {
      const d = list.find((x) => x._id === editId || x.id === editId);
      if (d) {
        setForm({ 
          name: d.name || '', 
          description: d.description || '',
          defaultFlowId: d.defaultFlowId || '',
          layout: d.layout || 'three-panel'
        });
        setDialogOpen(true);
      }
    }
  }, [editId, list]);

  const handleCreate = () => {
    setForm({ name: 'Untitled App', description: '', defaultFlowId: '', layout: 'three-panel' });
    setDialogOpen(true);
    if (flows.length === 0) loadFlows();
  };

  const handleCreateFromTemplate = () => {
    setTemplateDialogOpen(true);
    if (flows.length === 0) loadFlows();
  };

  const handleSaveFromTemplate = async (template) => {
    setSaving(true);
    const panels = (template.panels || []).map((p, i) => ({
      ...p,
      id: `${p.position}_${Date.now()}_${i}_${Math.random().toString(36).slice(2, 8)}`,
    }));
    const { data, error: err } = await createDashboardConfig({
      name: template.name || 'Untitled App',
      description: template.description || '',
      defaultFlowId: template.defaultFlowId || null,
      layout: template.layout || 'three-panel',
      chatPosition: template.chatPosition || 'center',
      panels,
    });
    setSaving(false);
    if (err) {
      setError(err);
      return;
    }
    setTemplateDialogOpen(false);
    load();
    if (data?._id || data?.id) navigate(`/dashboard-builder/edit/${data._id || data.id}`);
  };

  const handleDuplicate = async (id) => {
    setSaving(true);
    const { data, error: err } = await duplicateDashboardConfig(id);
    setSaving(false);
    if (err) {
      setError(err);
      return;
    }
    load();
    if (data?._id || data?.id) navigate(`/dashboard-builder/edit/${data._id || data.id}`);
  };

  const handleOpen = () => {
    setOpenDialogOpen(true);
  };

  const handlePreview = async (dashboardId) => {
    try {
      const { data } = await getDashboardConfig(dashboardId);
      setPreviewDashboard(data);
      setPreviewDialogOpen(true);
    } catch (err) {
      setError(err?.message || 'Failed to load dashboard for preview');
    }
  };

  const handleSaveNew = async () => {
    setSaving(true);
    const { data, error: err } = await createDashboardConfig({
      name: form.name || 'Untitled App',
      description: form.description || '',
      defaultFlowId: form.defaultFlowId || null,
      layout: form.layout || 'three-panel',
      chatPosition: form.chatPosition || 'header',
    });
    setSaving(false);
    if (err) {
      setError(err);
      return;
    }
    setDialogOpen(false);
    load();
    if (data?._id) navigate(`/dashboard-builder/edit/${data._id}`);
  };

  const handleUpdate = async (id) => {
    setSaving(true);
    const { error: err } = await updateDashboardConfig(id, {
      name: form.name,
      description: form.description || '',
      defaultFlowId: form.defaultFlowId || null,
      layout: form.layout || 'three-panel',
    });
    setSaving(false);
    if (err) {
      setError(err);
      return;
    }
    setDialogOpen(false);
    load();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this dashboard?')) return;
    const { error: err } = await deleteDashboardConfig(id);
    if (err) setError(err);
    else load();
  };

  const openPanelConfig = (dashboard, panel) => {
    const ct = panel.componentType || 'table';
    setPanelForm({
      dashboardId: dashboard._id,
      panelId: panel.id,
      componentType: ct,
      flowId: panel.flowId || '',
      outputKey: panel.outputKey || getDefaultOutputKey(ct),
      chartType: panel.config?.chartType || 'bar',
      refreshOnMount: panel.config?.refreshOnMount === true,
    });
    setPanelDialogOpen(true);
    // Reload flows if empty
    if (flows.length === 0) {
      loadFlows();
    }
  };

  const savePanelConfig = async () => {
    if (!panelForm?.dashboardId || !panelForm?.panelId) {
      setPanelDialogOpen(false);
      return;
    }
    const d = list.find((x) => (x._id || x.id) === panelForm.dashboardId);
    if (!d?.panels) {
      setPanelDialogOpen(false);
      return;
    }
    const panels = d.panels.map((p) => {
      if (p.id !== panelForm.panelId || p.type === 'chat') return p;
      return {
        ...p,
        componentType: panelForm.componentType,
        flowId: panelForm.flowId || null,
        outputKey: panelForm.outputKey || null,
        config: {
          ...(p.config || {}),
          chartType: panelForm.componentType === 'chart' ? (panelForm.chartType || 'bar') : undefined,
          refreshOnMount: panelForm.refreshOnMount === true,
        },
      };
    });
    const { error: err } = await updateDashboardConfig(panelForm.dashboardId, { panels, layout: 'dynamic' });
    if (err) setError(err);
    else load();
    setPanelDialogOpen(false);
    setPanelForm(null);
  };

  const handleAddPanel = (dashboard) => {
    setAddPanelForm({
      dashboardId: dashboard._id || dashboard.id,
      position: 'left',
      componentType: 'table',
      outputKey: 'table_data',
      flowId: '',
      chartType: 'bar',
    });
    setAddPanelDialogOpen(true);
    // Reload flows if empty
    if (flows.length === 0) {
      loadFlows();
    }
  };

  const saveAddPanel = async () => {
    if (!addPanelForm.dashboardId) {
      setAddPanelDialogOpen(false);
      return;
    }
    const d = list.find((x) => (x._id || x.id) === addPanelForm.dashboardId);
    if (!d) {
      setAddPanelDialogOpen(false);
      return;
    }
    const existingPanels = d.panels || [];
    const positionPanels = existingPanels.filter((p) => p.position === addPanelForm.position);
    const newPanel = {
      id: `${addPanelForm.position}_${Date.now()}`,
      type: 'component',
      componentType: addPanelForm.componentType,
      flowId: addPanelForm.flowId || null,
      outputKey: addPanelForm.outputKey,
      config: {
        ...(addPanelForm.componentType === 'chart' ? { chartType: addPanelForm.chartType || 'bar' } : {}),
        refreshOnMount: addPanelForm.refreshOnMount === true,
      },
      position: addPanelForm.position,
      order: positionPanels.length,
    };
    const panels = [...existingPanels, newPanel];
    const { error: err } = await updateDashboardConfig(addPanelForm.dashboardId, { panels, layout: 'dynamic' });
    if (err) setError(err);
    else load();
    setAddPanelDialogOpen(false);
    setAddPanelForm({ dashboardId: null, position: 'left', componentType: 'table', outputKey: 'table_data', flowId: '', chartType: 'bar', refreshOnMount: false });
  };

  const handleRemovePanel = async (dashboard, panelId) => {
    if (!window.confirm('Remove this panel?')) return;
    const d = list.find((x) => (x._id || x.id) === dashboard._id || dashboard.id);
    if (!d?.panels) return;
    const panel = d.panels.find((p) => p.id === panelId);
    if (panel?.fixed || panel?.type === 'chat') {
      setError('Cannot remove fixed chat panel');
      return;
    }
    const panels = d.panels.filter((p) => p.id !== panelId);
    const { error: err } = await updateDashboardConfig(d._id || d.id, { panels, layout: panels.length > 3 ? 'dynamic' : 'three-panel' });
    if (err) setError(err);
    else load();
  };

  return (
    <Box sx={{ p: 3, maxWidth: 720, mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h5">App Builder</Typography>
        <Stack direction="row" spacing={1}>
          <Button startIcon={<FolderOpenIcon />} variant="outlined" onClick={handleOpen}>
            Open
          </Button>
          <Button startIcon={<AddIcon />} variant="outlined" onClick={handleCreateFromTemplate}>
            From template
          </Button>
          <Button startIcon={<AddIcon />} variant="contained" onClick={handleCreate}>
            New App
          </Button>
        </Stack>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Click <strong>Edit</strong> on an app to configure it and set panel properties in the drawer. Click <strong>Run</strong> to use the app.
      </Typography>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <List>
          {list.map((d) => {
            const leftPanels = (d.panels || []).filter((p) => p.position === 'left');
            const rightPanels = (d.panels || []).filter((p) => p.position === 'right');
            const centerPanel = (d.panels || []).find((p) => p.position === 'center');
            return (
              <ListItem key={d._id || d.id} divider>
                <ListItemText
                  primary={d.name || 'Untitled Dashboard'}
                  secondary={
                    <Box>
                      <Typography variant="caption" display="block">
                        {d.layout === 'three-panel' ? '3-panel (L | Chat | R)' : `Dynamic (${leftPanels.length}L | Chat | ${rightPanels.length}R)`}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                        {leftPanels.map((p) => (
                          <Box key={p.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography variant="caption" color="text.secondary">
                              L: {p.componentType || 'none'}
                            </Typography>
                            <IconButton
                              size="small"
                              onClick={() => openPanelConfig(d, p)}
                              sx={{ p: 0.25 }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            {!p.fixed && (
                              <IconButton
                                size="small"
                                onClick={() => handleRemovePanel(d, p.id)}
                                sx={{ p: 0.25 }}
                              >
                                <RemoveIcon fontSize="small" />
                              </IconButton>
                            )}
                          </Box>
                        ))}
                        {rightPanels.map((p) => (
                          <Box key={p.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography variant="caption" color="text.secondary">
                              R: {p.componentType || 'none'}
                            </Typography>
                            <IconButton
                              size="small"
                              onClick={() => openPanelConfig(d, p)}
                              sx={{ p: 0.25 }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            {!p.fixed && (
                              <IconButton
                                size="small"
                                onClick={() => handleRemovePanel(d, p.id)}
                                sx={{ p: 0.25 }}
                              >
                                <RemoveIcon fontSize="small" />
                              </IconButton>
                            )}
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  }
                />
                <ListItemSecondaryAction>
                  <Tooltip title="Edit app (configure panels)">
                    <IconButton
                      edge="end"
                      size="small"
                      onClick={() => navigate(`/dashboard-builder/edit/${d._id || d.id}`)}
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Add panel">
                    <IconButton edge="end" size="small" onClick={() => handleAddPanel(d)}>
                      <AddIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Duplicate">
                    <IconButton edge="end" size="small" onClick={() => handleDuplicate(d._id || d.id)} disabled={saving}>
                      <ContentCopyIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Preview">
                    <IconButton edge="end" size="small" onClick={() => handlePreview(d._id || d.id)}>
                      <VisibilityIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Run app (play mode)">
                    <IconButton
                      edge="end"
                      size="small"
                      onClick={() => navigate(`/dashboard-builder/run/${d._id || d.id}`)}
                    >
                      <PlayArrowIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton edge="end" size="small" onClick={() => handleDelete(d._id || d.id)}>
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </ListItemSecondaryAction>
              </ListItem>
            );
          })}
        </List>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editId ? 'Edit App' : 'New App'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="App Name"
            fullWidth
            required
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Description"
            fullWidth
            multiline
            rows={3}
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            placeholder="Describe what this app is for..."
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth margin="dense" sx={{ mb: 2 }}>
            <InputLabel>Layout</InputLabel>
            <Select
              value={form.layout || 'three-panel'}
              label="Layout"
              onChange={(e) => setForm((f) => ({ ...f, layout: e.target.value }))}
            >
              <MenuItem value="three-panel">Three Panel (L | Chat | R)</MenuItem>
              <MenuItem value="dynamic">Dynamic (Multiple Panels)</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth margin="dense">
            <InputLabel>Default Flow</InputLabel>
            <Select
              value={form.defaultFlowId || ''}
              label="Default Flow"
              onChange={(e) => setForm((f) => ({ ...f, defaultFlowId: e.target.value }))}
              disabled={flowsLoading}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {flowsLoading ? (
                <MenuItem disabled>
                  <CircularProgress size={16} sx={{ mr: 1 }} />
                  Loading flows...
                </MenuItem>
              ) : flows.length === 0 ? (
                <MenuItem disabled>No flows available</MenuItem>
              ) : (
                flows.map((f) => (
                  <MenuItem key={f._id || f.id} value={f._id || f.id}>
                    {f.name || 'Untitled flow'}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => (editId ? handleUpdate(editId) : handleSaveNew())}
            disabled={saving || !form.name}
          >
            {saving ? <CircularProgress size={20} /> : editId ? 'Save' : 'Create (then configure panels)'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Open App Dialog */}
      <Dialog open={openDialogOpen} onClose={() => setOpenDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Open App</DialogTitle>
        <DialogContent>
          {!loading && list.length > 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Click a row to run the app, or click the pencil icon to edit it.
            </Typography>
          )}
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : list.length === 0 ? (
            <Alert severity="info" sx={{ mt: 2 }}>
              No apps available. Create a new app to get started.
            </Alert>
          ) : (
            <List>
              {list.map((d) => (
                <ListItem
                  key={d._id || d.id}
                  button
                  onClick={() => {
                    setOpenDialogOpen(false);
                    navigate(`/dashboard-builder/run/${d._id || d.id}`);
                  }}
                >
                  <ListItemText
                    primary={d.name || 'Untitled App'}
                    secondary={d.description || 'No description'}
                  />
                  <IconButton edge="end" title="Edit app" onClick={(e) => {
                    e.stopPropagation();
                    setOpenDialogOpen(false);
                    navigate(`/dashboard-builder/edit/${d._id || d.id}`);
                  }}>
                    <EditIcon />
                  </IconButton>
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Template Gallery Dialog */}
      <Dialog open={templateDialogOpen} onClose={() => setTemplateDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Create app from template</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Choose a template to get started quickly. You can customize everything after creating.
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {APP_TEMPLATES.map((t) => (
              <Box
                key={t.id}
                onClick={() => handleSaveFromTemplate(t)}
                sx={{
                  p: 2,
                  minWidth: 200,
                  maxWidth: 260,
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 2,
                  cursor: 'pointer',
                  '&:hover': { bgcolor: 'action.hover', borderColor: 'primary.main' },
                }}
              >
                <Typography variant="subtitle1" fontWeight="600">{t.name}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {t.description}
                </Typography>
                <Chip label={t.layout} size="small" sx={{ mt: 1 }} />
              </Box>
            ))}
          </Box>
          {saving && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <CircularProgress />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTemplateDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Preview App Dialog */}
      <Dialog open={previewDialogOpen} onClose={() => setPreviewDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <VisibilityIcon />
            <Typography variant="h6">Preview App</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          {previewDashboard ? (
            <Box>
              <Typography variant="h6" gutterBottom>{previewDashboard.name || 'Untitled App'}</Typography>
              {previewDashboard.description && (
                <Typography variant="body2" color="text.secondary" paragraph>
                  {previewDashboard.description}
                </Typography>
              )}
              <Divider sx={{ my: 2 }} />
              <Stack spacing={2}>
                <Box>
                  <Typography variant="subtitle2" gutterBottom>Layout:</Typography>
                  <Chip label={previewDashboard.layout || 'three-panel'} size="small" />
                </Box>
                {previewDashboard.defaultFlowId && (
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>Default Flow:</Typography>
                    <Chip 
                      label={flows.find(f => (f._id || f.id) === previewDashboard.defaultFlowId)?.name || 'Unknown'} 
                      size="small" 
                    />
                  </Box>
                )}
                <Box>
                  <Typography variant="subtitle2" gutterBottom>Panels:</Typography>
                  <Typography variant="body2">
                    {previewDashboard.panels?.length || 0} panel(s) configured
                  </Typography>
                </Box>
              </Stack>
            </Box>
          ) : (
            <CircularProgress />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewDialogOpen(false)}>Close</Button>
          {previewDashboard && (
            <Button
              variant="contained"
              startIcon={<PlayArrowIcon />}
              onClick={() => {
                setPreviewDialogOpen(false);
                navigate(`/dashboard-builder/run/${previewDashboard._id || previewDashboard.id}`);
              }}
            >
              Run App
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={panelDialogOpen} onClose={() => setPanelDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit panel</DialogTitle>
        <DialogContent>
          {panelForm && (
            <>
              <FormControl fullWidth margin="dense">
                <InputLabel>Component type</InputLabel>
                <Select
                  value={panelForm.componentType || 'table'}
                  label="Component type"
                  onChange={(e) => {
                    const t = e.target.value;
                    setPanelForm((f) => ({ ...f, componentType: t, outputKey: getDefaultOutputKey(t) }));
                  }}
                >
                  {COMPONENT_TYPES.map((t) => (
                    <MenuItem key={t} value={t}>{COMPONENT_REGISTRY[t]?.label || t}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              {panelForm.componentType === 'chart' && (
                <FormControl fullWidth margin="dense">
                  <InputLabel>Chart type</InputLabel>
                  <Select
                    value={panelForm.chartType || 'bar'}
                    label="Chart type"
                    onChange={(e) => setPanelForm((f) => ({ ...f, chartType: e.target.value }))}
                  >
                    <MenuItem value="bar">Bar</MenuItem>
                    <MenuItem value="line">Line</MenuItem>
                    <MenuItem value="pie">Pie</MenuItem>
                    <MenuItem value="area">Area</MenuItem>
                  </Select>
                </FormControl>
              )}
              <FormControl fullWidth margin="dense">
                <InputLabel>Output key</InputLabel>
                <Select
                  value={panelForm.outputKey || 'table_data'}
                  label="Output key"
                  onChange={(e) => setPanelForm((f) => ({ ...f, outputKey: e.target.value }))}
                >
                  {OUTPUT_KEYS.map((k) => (
                    <MenuItem key={k} value={k}>{k}</MenuItem>
                  ))}
                </Select>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  Flow must return this key with matching data shape
                </Typography>
              </FormControl>
              <FormControl fullWidth margin="dense">
                <InputLabel>Flow</InputLabel>
                <Select
                  value={panelForm.flowId || ''}
                  label="Flow"
                  onChange={(e) => setPanelForm((f) => ({ ...f, flowId: e.target.value }))}
                  disabled={flowsLoading}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {flowsLoading ? (
                    <MenuItem disabled>
                      <CircularProgress size={16} sx={{ mr: 1 }} />
                      Loading flows...
                    </MenuItem>
                  ) : flows.length === 0 ? (
                    <MenuItem disabled>No flows available</MenuItem>
                  ) : (
                    flows.map((f) => (
                      <MenuItem key={f._id || f.id} value={f._id || f.id}>
                        {f.name || 'Untitled flow'}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={panelForm.refreshOnMount === true}
                    onChange={(e) => setPanelForm((f) => ({ ...f, refreshOnMount: e.target.checked }))}
                  />
                }
                label="Refresh on load (run flow when app opens)"
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPanelDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={savePanelConfig}>Save</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={addPanelDialogOpen} onClose={() => setAddPanelDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Panel</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="dense">
            <InputLabel>Position</InputLabel>
            <Select
              value={addPanelForm.position}
              label="Position"
              onChange={(e) => setAddPanelForm((f) => ({ ...f, position: e.target.value }))}
            >
              <MenuItem value="left">Left</MenuItem>
              <MenuItem value="right">Right</MenuItem>
              <MenuItem value="top">Top</MenuItem>
              <MenuItem value="bottom">Bottom</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth margin="dense">
            <InputLabel>Component type</InputLabel>
            <Select
              value={addPanelForm.componentType}
              label="Component type"
              onChange={(e) => {
                const t = e.target.value;
                setAddPanelForm((f) => ({ ...f, componentType: t, outputKey: getDefaultOutputKey(t) }));
              }}
            >
              {COMPONENT_TYPES.map((t) => (
                <MenuItem key={t} value={t}>{COMPONENT_REGISTRY[t]?.label || t}</MenuItem>
              ))}
            </Select>
          </FormControl>
          {addPanelForm.componentType === 'chart' && (
            <FormControl fullWidth margin="dense">
              <InputLabel>Chart type</InputLabel>
              <Select
                value={addPanelForm.chartType || 'bar'}
                label="Chart type"
                onChange={(e) => setAddPanelForm((f) => ({ ...f, chartType: e.target.value }))}
              >
                <MenuItem value="bar">Bar</MenuItem>
                <MenuItem value="line">Line</MenuItem>
                <MenuItem value="pie">Pie</MenuItem>
                <MenuItem value="area">Area</MenuItem>
              </Select>
            </FormControl>
          )}
          <FormControl fullWidth margin="dense">
            <InputLabel>Output key</InputLabel>
            <Select
              value={addPanelForm.outputKey}
              label="Output key"
              onChange={(e) => setAddPanelForm((f) => ({ ...f, outputKey: e.target.value }))}
            >
              {OUTPUT_KEYS.map((k) => (
                <MenuItem key={k} value={k}>{k}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="dense">
            <InputLabel>Flow</InputLabel>
            <Select
              value={addPanelForm.flowId || ''}
              label="Flow"
              onChange={(e) => setAddPanelForm((f) => ({ ...f, flowId: e.target.value }))}
              disabled={flowsLoading}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {flowsLoading ? (
                <MenuItem disabled>
                  <CircularProgress size={16} sx={{ mr: 1 }} />
                  Loading flows...
                </MenuItem>
              ) : flows.length === 0 ? (
                <MenuItem disabled>No flows available</MenuItem>
              ) : (
                flows.map((f) => (
                  <MenuItem key={f._id || f.id} value={f._id || f.id}>
                    {f.name || 'Untitled flow'}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Checkbox
                checked={addPanelForm.refreshOnMount === true}
                onChange={(e) => setAddPanelForm((f) => ({ ...f, refreshOnMount: e.target.checked }))}
              />
            }
            label="Refresh on load (run flow when app opens)"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddPanelDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveAddPanel}>Add Panel</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
